import { Dialog } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CourseService } from '@app/core/services/course.service';
import { notNull } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { BehaviorSubject, map, Observable, take, tap } from 'rxjs';
import { Course } from '../../models';
import { TrailsService } from '../../services';
import { ModalEventsComponent } from '../modal-events/modal-events.component';
import { ModalVoucherComponent } from '../modal-voucher/modal-voucher.component';

@Component({
  selector: 'app-card-course',
  templateUrl: './card-course.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CardCourseComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly _dialog = inject(Dialog);
  private readonly trailsService = inject(TrailsService);
  private readonly _courseService = inject(CourseService);
  private readonly store = inject<Store<AppState>>(Store);

  @Input() public isShowcase = false;
  @Input() public redirect: string;
  @Input() public cardWidth = 80;
  @Input() public openCourseId: number;

  @Output() public openCourse = new EventEmitter<Course>();

  public readonly empty = 'assets/icons/icon-default.svg';
  public course: Course;
  public formSubmitAttempt: boolean;
  public token$: Observable<string>;
  public activeUser$ = new BehaviorSubject<any[]>([]);
  private entitiesSubject$ = new BehaviorSubject<number | null>(null);
  public entities$ = this.entitiesSubject$.asObservable();
  public entidade!: number;
  public entidadeValue: number | null = null;
  public selectedMobile: boolean = false;

  public voucher = new FormControl(undefined, [
    Validators.required,
    Validators.maxLength(18),
    Validators.minLength(18),
  ]);

  constructor() {
    this.entidadeValue = this.entitiesSubject$.getValue();
  }

  @Input('course') public set setCourse(course: Course) {
    this.course = course;

    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(
        notNull(),
        map((activeUser) => activeUser?.escola_externa),
      )
      .subscribe((external) => {
        this.course.enable = this.course?.enable || external ? 1 : 0;
        this.entitiesSubject$.next(external);
      });
  }

  public ngOnInit(): void {
    this.token$ = this.store.select(AppSelectors.token).pipe(take(1));
    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(
        notNull(),
        tap((activeUser) => this.activeUser$.next(activeUser)),
      )
      .subscribe({});

    this.entities$.subscribe((entity) => {
      this.entidade = entity;
    });
  }

  public onOpenModal() {
    this._dialog
      .open<boolean>(ModalVoucherComponent, {
        data: { course: this.course },
      })
      .closed.pipe(notNull())
      .subscribe(() => this.onOpenCourse(this.course));
  }

  public onOpenReportTemp(slugCourse: string, token: string): void {
    this.router.navigate([`/${this.getUrlUcIvca()}/relatorio`, slugCourse]);
  }

  public onOpenCourse(course: Course): void {
    if (this.redirect) {
      this.router.navigateByUrl(`/${this.getUrlUcIvca()}/cursos`);
    } else if (!!course?.subdivisoes?.length || !!course?.trilhas?.length) {
      this.openCourse.emit(course);
    } else if (this.isShowcase) {
      this.router.navigate([this.getUrlUcIvca(), 'vitrine', course.slug]);
    } else {
      this.router.navigate([this.getUrlUcIvca(), 'cursos', course.evento?.id, course.slug]);
    }
  }

  public onOpenEvents() {
    return this._dialog
      .open(ModalEventsComponent, {
        width: '95vw',
        height: '95vh',
        data: { course: { ...this.course, selected: true } },
      })
      .closed.subscribe();
  }

  public withdrawCertificate(eventoId: number, cursoSlug: string, token: string) {
    this._courseService.withdrawCertificate(eventoId, cursoSlug, token, this.activeUser$.value);
  }

  public onOpenCourseMenu() {
    this.selectedMobile = true;
  }

  public onCloseCourseMenu() {
    this.selectedMobile = false;
  }

  private getUrlUcIvca(): string {
    return this.trailsService.uc$.value
      ? 'universidade-corporativa'
      : this.trailsService.ivca$.value
        ? 'ivca'
        : 'trilhas-de-aprendizagem';
  }
}
